/**
 * Panel copy for the file-request feature. The JS half of a two-sided map.
 *
 * THE OTHER HALF IS `backend/app/file_request_copy.py`, and it is canonical: it
 * additionally holds every email string, and two sentences here are rendered by
 * BOTH a panel and an email —
 *
 *     client_panel.at_risk            === mail_client.at_risk
 *     editor_panel.cannot_provide_next === mail_editor.cannot_provide_next
 *
 * Those pairs are why this file exists in this shape. Four hand-maintained copies
 * of two sentences had already drifted before anyone noticed ("close this" vs
 * "close the request", three spellings of "the client says the files are
 * uploaded"), and nothing failed — copy drift throws nothing and logs nothing.
 *
 * WHAT KEEPS THEM HONEST: `backend/tests/test_file_request_copy_parity.py` parses
 * this file and asserts, entry for entry, that the two sides agree — including
 * that neither side has a key or a category override the other lacks. It fails at
 * build time, which is the point: an earlier draft served this map from the API
 * instead, which caught drift at runtime and added a way for a stale backend to
 * blank three panels. A test that fails in CI beats a request that fails in front
 * of a client.
 *
 * SO: edit the Python file, then mirror the change here, and the test will tell
 * you if you missed one. Do not add a key here that does not exist there.
 *
 * There is deliberately NO frame-plus-noun accessor. Each render owns a complete
 * sentence; the old `proseFor` dropped a category noun into "You asked for ___",
 * which is what produced "You asked for a question about the files".
 */
export const FILE_REQUEST_COPY = {
  "client_panel.slab": {
    "_default": "We need something from you"
  },
  "client_panel.at_risk": {
    "_default": "Your estimated delivery of {eta} is {at_risk} until we receive this.",
    "question_about": "Your estimated delivery of {eta} is {at_risk} until we hear back."
  },
  "client_panel.at_risk_no_eta": {
    "_default": "Your delivery is {at_risk} until we receive this.",
    "question_about": "Your delivery is {at_risk} until we hear back."
  },
  "client_panel.note_label": {
    "_default": "Where did you put them? Or tell us what you’re missing.",
    "question_about": "What’s your answer? Or tell us if you’re not sure."
  },
  "client_panel.note_placeholder": {
    "_default": "e.g. same Dropbox, new folder called Card 3",
    "question_about": "e.g. toasts in the highlight only, not the doc edit"
  },
  "client_panel.verdict_affirmative": {
    "_default": "They’re uploaded",
    "question_about": "Send my answer"
  },
  "client_panel.verdict_negative": {
    "_default": "I don’t have these",
    "question_about": "I can’t answer this"
  },
  "client_panel.either_answer": {
    "_default": "Either answer reaches us straight away."
  },
  "client_panel.answered_opener": {
    "_default": "Thanks for letting us know. We’ll go ahead without them.",
    "question_about": "Thanks for letting us know. We’ll make a judgement call and carry on."
  },
  "client_panel.your_reply_label": {
    "_default": "What you told us"
  },
  "client_panel.revise_label": {
    "_default": "Found them after all?",
    "question_about": "Thought of an answer?"
  },
  "client_panel.revise_placeholder": {
    "_default": "e.g. they were on the second card — uploaded now",
    "question_about": "e.g. checked with my partner — toasts in both"
  },
  "client_panel.closed_title": {
    "_default": "No longer needed"
  },
  "client_panel.closed_body": {
    "_default": "We’ve sorted this out — nothing needed from you.",
    "question_about": "We’ve sorted this out — no answer needed."
  },
  "client_panel.closed_dismiss": {
    "_default": "Dismiss"
  },
  "client_panel.note_required_uploaded": {
    "_default": "Tell us where — a folder name or a link, so your editor isn’t hunting.",
    "question_about": "Type your answer first — that’s the part your editor needs."
  },
  "client_panel.revise_button": {
    "_default": "They’re uploaded now",
    "question_about": "Answer it now"
  },
  "editor_panel.awaiting_title": {
    "_default": "Awaiting client"
  },
  "editor_panel.awaiting_sentence": {
    "_default": "You reported missing files on {date}. Carry on with what you have and we’ll let you know when they reply.",
    "audio_missing": "You reported missing audio on {date}. Carry on with what you have and we’ll let you know when they reply.",
    "folder_missing": "You reported a missing folder on {date}. Carry on with what you have and we’ll let you know when they reply.",
    "question_about": "You asked the client a question on {date}. Carry on with what you have and we’ll let you know when they reply."
  },
  "editor_panel.replied_title": {
    "_default": "Client replied"
  },
  "editor_panel.cannot_provide_title": {
    "_default": "The client can’t provide this.",
    "question_about": "The client can’t answer this."
  },
  "editor_panel.cannot_provide_next": {
    "_default": "Admin has been told. Close this or ask for something else."
  },
  "editor_panel.action_supersede": {
    "_default": "Ask for something else"
  },
  "editor_panel.action_resolve": {
    "_default": "I’ve got what I need"
  },
  "admin_panel.slab_cannot_provide": {
    "_default": "Client cannot provide these",
    "question_about": "Client cannot answer this"
  },
  "admin_panel.slab_stale": {
    "_default": "No answer in 48 hours"
  },
  "admin_panel.slab_open": {
    "_default": "File request open"
  },
  "admin_panel.reply_cannot_provide": {
    "_default": "Client can’t provide these",
    "question_about": "Client can’t answer this"
  },
  "admin_panel.no_reply_today": {
    "_default": "The client hasn’t answered yet — sent at {time}."
  },
  "admin_panel.no_reply_older": {
    "_default": "The client hasn’t answered yet — sent {date}."
  },
  "shared.no_note_uploaded_fallback": {
    "_default": "The client didn’t say where — if it’s not in the usual folder, ask in chat.",
    "question_about": "The client replied without writing an answer — follow up in chat."
  },
  "shared.trace_title": {
    "_default": "Last file request"
  },
  "shared.trace_no_reply": {
    "_default": "Closed without a client reply."
  },
  "shared.trace_res_client_uploaded": {
    "_default": "Resolved — the client said the files are uploaded.",
    "question_about": "Resolved — the client answered."
  },
  "shared.trace_res_editor_resolved": {
    "_default": "Closed by the editor — they got what they needed."
  },
  "shared.trace_res_admin_resolve": {
    "_default": "Resolved by the team."
  },
  "shared.trace_res_admin_cancel": {
    "_default": "Cancelled by the team — it shouldn’t have gone out."
  },
  "shared.trace_res_superseded": {
    "_default": "Replaced by a newer request."
  },
  "shared.client_said_label": {
    "_default": "What the client said"
  },
  "shared.cta": {
    "_default": "Open the project"
  },
  "shared.category_fallback": {
    "_default": "We need something"
  }
};

export default FILE_REQUEST_COPY;
